import React, { useState, useEffect, useRef } from 'react';
import { useSearchParams } from 'react-router-dom';
import { useReactToPrint } from 'react-to-print';
import { FileText, Plus, Download, Trash2, Edit, Eye, X, Calendar, ChevronLeft, Save, Printer, Sparkles } from 'lucide-react';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Input } from '../components/ui/input';
import { Textarea } from '../components/ui/textarea';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../components/ui/dialog';
import { toast } from 'sonner';
import api from '../lib/api';

const SECTION_LABELS = {
  what_hit_the_fan: "What Hit the Fan",
  what_we_avoided: "What We Avoided Hitting the Fan",
  security_improvements: "Security Posture Improvements Implemented",
  strategic_actions: "Strategic Actions Initiated",
  what_could_hit_next: "What Could Hit the Fan Next Week",
};

// Helper to get Monday of the current week
const getWeekStart = (date = new Date()) => {
  const d = new Date(date);
  const day = d.getDay();
  const diff = d.getDate() - day + (day === 0 ? -6 : 1); // Monday
  d.setDate(diff);
  d.setHours(0, 0, 0, 0);
  return d;
};

// Helper to get Sunday of the current week (end of week)
const getWeekEnd = (date = new Date()) => {
  const start = getWeekStart(date);
  const end = new Date(start);
  end.setDate(end.getDate() + 6);
  end.setHours(23, 59, 59, 999);
  return end;
};

const ReportEditor = ({ report, onSave, onCancel, isNew, aiInsights }) => {
  const weekStart = getWeekStart();
  const weekEnd = getWeekEnd();
  
  // Build initial what_hit_the_fan content from AI insights if provided
  const buildAiContent = () => {
    if (!aiInsights) return '';
    
    const { stats, narrative, period } = aiInsights;
    return `AI INSIGHTS (${period})

STATS SUMMARY:
• Total Tickets: ${stats.total_tickets} (${stats.vs_last_week >= 0 ? '+' : ''}${stats.vs_last_week} vs last week)
• Resolved: ${stats.resolved} (${stats.resolution_rate}% resolution rate)
• Still Open: ${stats.still_open}
• Avg Resolution Time: ${stats.avg_resolution_hours} hours
${stats.sla_breaches > 0 ? `• SLA Breaches: ${stats.sla_breaches}\n` : ''}
TOP CATEGORIES:
${stats.top_categories?.slice(0, 5).map(cat => `• ${cat.name}: ${cat.count}`).join('\n') || 'N/A'}

AI ANALYSIS:
${narrative}

---
`;
  };
  
  const [formData, setFormData] = useState({
    title: report?.title || (aiInsights ? `FAN Report – Week of ${weekStart.toLocaleDateString('en-US')}` : ''),
    week_of: report?.week_of || weekStart.toLocaleDateString('en-US'),
    week_start: report?.week_start || weekStart.toISOString(),
    week_end: report?.week_end || weekEnd.toISOString(),
    what_hit_the_fan: report?.what_hit_the_fan || buildAiContent(),
    what_we_avoided: report?.what_we_avoided || '',
    security_improvements: report?.security_improvements || '',
    strategic_actions: report?.strategic_actions || '',
    what_could_hit_next: report?.what_could_hit_next || '',
  });
  const [saving, setSaving] = useState(false);

  const handleChange = (field, value) => {
    setFormData(prev => ({ ...prev, [field]: value }));
  };

  // Update date range when week_of changes
  const handleWeekOfChange = (value) => {
    setFormData(prev => {
      const newData = { ...prev, week_of: value };
      // Try to parse and set week range
      try {
        const parts = value.split('/');
        if (parts.length === 3) {
          const date = new Date(parts[2], parts[0] - 1, parts[1]);
          if (!isNaN(date.getTime())) {
            const start = getWeekStart(date);
            const end = getWeekEnd(date);
            newData.week_start = start.toISOString();
            newData.week_end = end.toISOString();
          }
        }
      } catch (e) {
        // Keep existing dates if parsing fails
      }
      return newData;
    });
  };

  const handleSubmit = async () => {
    if (!formData.title.trim()) {
      alert('Please enter a report title');
      return;
    }
    setSaving(true);
    try {
      await onSave(formData);
    } finally {
      setSaving(false);
    }
  };

  // Format date range for display
  const formatDateRange = () => {
    try {
      const start = new Date(formData.week_start);
      const end = new Date(formData.week_end);
      return `${start.toLocaleDateString('en-US')} - ${end.toLocaleDateString('en-US')}`;
    } catch (e) {
      return 'Invalid date range';
    }
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onCancel} className="gap-2">
          <ChevronLeft className="h-4 w-4" />
          Back to Reports
        </Button>
        <Button onClick={handleSubmit} disabled={saving} className="gap-2 bg-blue-600 hover:bg-blue-700">
          <Save className="h-4 w-4" />
          {saving ? 'Saving...' : (isNew ? 'Create Report' : 'Save Changes')}
        </Button>
      </div>

      <Card>
        <CardHeader>
          <CardTitle>{isNew ? 'Create New FAN Report' : 'Edit FAN Report'}</CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Report Title</label>
              <Input
                value={formData.title}
                onChange={(e) => handleChange('title', e.target.value)}
                placeholder="FAN Report – Week of..."
              />
            </div>
            <div>
              <label className="block text-sm font-medium text-gray-700 mb-1">Week Of (Start Date)</label>
              <Input
                value={formData.week_of}
                onChange={(e) => handleWeekOfChange(e.target.value)}
                placeholder="2/9/2026"
              />
              <p className="text-xs text-gray-500 mt-1">
                Stats will be captured for: <strong>{formatDateRange()}</strong>
              </p>
            </div>
          </div>

          {Object.entries(SECTION_LABELS).map(([key, label]) => (
            <div key={key}>
              <label className="block text-sm font-medium text-gray-700 mb-1">{label}</label>
              <Textarea
                value={formData[key]}
                onChange={(e) => handleChange(key, e.target.value)}
                placeholder={`Enter ${label.toLowerCase()}...`}
                className="min-h-[150px] font-mono text-sm"
              />
              <p className="text-xs text-gray-500 mt-1">Use bullet points (•) for list items</p>
            </div>
          ))}
        </CardContent>
      </Card>
    </div>
  );
};

const ReportView = ({ report, onBack, onEdit }) => {
  const printRef = useRef();
  
  const handlePrint = useReactToPrint({
    contentRef: printRef,
    documentTitle: `FAN_Report_${report.week_of.replace(/\//g, '-')}`,
  });

  const formatContent = (content) => {
    if (!content) return null;
    return content.split('\n').map((line, i) => {
      const trimmed = line.trim();
      if (trimmed.startsWith('•')) {
        return <li key={i} className="ml-4">{trimmed.substring(1).trim()}</li>;
      }
      if (trimmed) {
        return <p key={i} className="mb-2">{trimmed}</p>;
      }
      return <br key={i} />;
    });
  };

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between">
        <Button variant="ghost" onClick={onBack} className="gap-2">
          <ChevronLeft className="h-4 w-4" />
          Back to Reports
        </Button>
        <div className="flex gap-2">
          <Button variant="outline" onClick={onEdit} className="gap-2">
            <Edit className="h-4 w-4" />
            Edit
          </Button>
          <Button onClick={handlePrint} className="gap-2 bg-blue-600 hover:bg-blue-700">
            <Printer className="h-4 w-4" />
            Export PDF
          </Button>
        </div>
      </div>

      <div ref={printRef} className="bg-white p-8 print:p-0">
        <div className="max-w-4xl mx-auto">
          <div className="border-b-2 border-gray-200 pb-4 mb-6">
            <h1 className="text-3xl font-bold text-gray-900">{report.title}</h1>
            <p className="text-gray-500 mt-1">Week of {report.week_of}</p>
            {report.stats_snapshot && (
              <div className="mt-4 flex flex-wrap gap-4 text-sm">
                <span className="bg-gray-100 px-3 py-1 rounded">
                  Total Tickets: <strong>{report.stats_snapshot.total_tickets}</strong>
                </span>
                <span className="bg-red-100 text-red-700 px-3 py-1 rounded">
                  Open: <strong>{report.stats_snapshot.open}</strong>
                </span>
                <span className="bg-green-100 text-green-700 px-3 py-1 rounded">
                  Closed: <strong>{report.stats_snapshot.closed}</strong>
                </span>
                <span className="bg-orange-100 text-orange-700 px-3 py-1 rounded">
                  High Priority: <strong>{report.stats_snapshot.high_priority_open}</strong>
                </span>
                <span className="bg-blue-100 text-blue-700 px-3 py-1 rounded">
                  Avg Close Time: <strong>{
                    (() => {
                      const mins = report.stats_snapshot.avg_resolution_minutes || 0;
                      if (mins <= 0) return 'N/A';
                      if (mins < 60) return `${Math.round(mins)}m`;
                      if (mins < 1440) return `${Math.round(mins / 60)}h`;
                      return `${Math.round(mins / 1440)}d ${Math.round((mins % 1440) / 60)}h`;
                    })()
                  }</strong>
                </span>
              </div>
            )}
          </div>

          {/* Assignee Stats Table */}
          {report.stats_snapshot?.by_assignee && report.stats_snapshot.by_assignee.length > 0 && (
            <section className="mb-8">
              <h2 className="text-xl font-semibold text-gray-800 mb-3 border-l-4 border-blue-500 pl-3">
                Tickets by Assignee
              </h2>
              <div className="overflow-x-auto">
                <table className="w-full text-sm">
                  <thead>
                    <tr className="border-b border-gray-200 text-left">
                      <th className="py-2 pr-4 font-semibold text-gray-700">Resource</th>
                      <th className="py-2 px-4 font-semibold text-gray-700 text-center">Total</th>
                      <th className="py-2 px-4 font-semibold text-gray-700 text-center">Active Workload</th>
                      <th className="py-2 px-4 font-semibold text-gray-700 text-center">Open</th>
                      <th className="py-2 px-4 font-semibold text-gray-700 text-center">In Progress</th>
                      <th className="py-2 px-4 font-semibold text-gray-700 text-center">Closed</th>
                      <th className="py-2 px-4 font-semibold text-gray-700 text-right">Avg Close Time</th>
                    </tr>
                  </thead>
                  <tbody>
                    {report.stats_snapshot.by_assignee.map((assignee, idx) => {
                      // Convert minutes to human readable format
                      const avgMins = assignee.avg_resolve || 0;
                      let avgTimeStr = 'N/A';
                      if (avgMins > 0) {
                        if (avgMins < 60) {
                          avgTimeStr = `${Math.round(avgMins)}m`;
                        } else if (avgMins < 1440) {
                          avgTimeStr = `${Math.round(avgMins / 60)}h`;
                        } else {
                          avgTimeStr = `${Math.round(avgMins / 1440)}d ${Math.round((avgMins % 1440) / 60)}h`;
                        }
                      }
                      const activeWorkload = (assignee.open || 0) + (assignee.in_progress || 0);
                      return (
                        <tr key={idx} className="border-b border-gray-100">
                          <td className="py-2 pr-4 font-medium text-gray-900">{assignee.name}</td>
                          <td className="py-2 px-4 text-center">{assignee.total}</td>
                          <td className="py-2 px-4 text-center font-semibold text-orange-600">{activeWorkload}</td>
                          <td className="py-2 px-4 text-center text-blue-600">{assignee.open || 0}</td>
                          <td className="py-2 px-4 text-center text-yellow-600">{assignee.in_progress || 0}</td>
                          <td className="py-2 px-4 text-center text-green-600">{assignee.closed}</td>
                          <td className="py-2 px-4 text-right text-gray-600">{avgTimeStr}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            </section>
          )}

          {Object.entries(SECTION_LABELS).map(([key, label]) => (
            report[key] && (
              <section key={key} className="mb-8">
                <h2 className="text-xl font-semibold text-gray-800 mb-3 border-l-4 border-blue-500 pl-3">
                  {label}
                </h2>
                <div className="text-gray-700 leading-relaxed">
                  {formatContent(report[key])}
                </div>
              </section>
            )
          ))}

          <div className="border-t border-gray-200 pt-4 mt-8 text-sm text-gray-500">
            <p>Generated: {new Date(report.created_at).toLocaleString()}</p>
            {report.updated_at !== report.created_at && (
              <p>Last Updated: {new Date(report.updated_at).toLocaleString()}</p>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

const ReportsPage = () => {
  const [searchParams] = useSearchParams();
  const [reports, setReports] = useState([]);
  const [loading, setLoading] = useState(true);
  const [view, setView] = useState('list'); // 'list', 'create', 'edit', 'view'
  const [selectedReport, setSelectedReport] = useState(null);
  const [deleteConfirm, setDeleteConfirm] = useState(null);
  const [aiInsights, setAiInsights] = useState(null);

  useEffect(() => {
    fetchReports();
    
    // Check if we should open create view with AI insights
    if (searchParams.get('add_ai_insights') === 'true') {
      const stored = sessionStorage.getItem('ai_insights_for_fan');
      if (stored) {
        try {
          const insights = JSON.parse(stored);
          setAiInsights(insights);
          setView('create');
          sessionStorage.removeItem('ai_insights_for_fan');
          toast.success('AI Insights loaded! Create your FAN Report.');
        } catch (e) {
          console.error('Failed to parse AI insights:', e);
        }
      }
    }
  }, [searchParams]);

  const fetchReports = async () => {
    setLoading(true);
    try {
      const res = await api.getReports({ limit: 50 });
      setReports(res.data.reports || []);
    } catch (err) {
      console.error('Failed to fetch reports:', err);
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async (formData) => {
    try {
      const res = await api.createReport({
        ...formData,
        title: formData.title || `FAN Report – Week of ${formData.week_of}`,
      });
      setReports(prev => [res.data, ...prev]);
      setSelectedReport(res.data);
      setView('view');
    } catch (err) {
      console.error('Failed to create report:', err);
      alert('Failed to create report');
    }
  };

  const handleUpdate = async (formData) => {
    try {
      const res = await api.updateReport(selectedReport.report_id, formData);
      setReports(prev => prev.map(r => r.report_id === selectedReport.report_id ? res.data : r));
      setSelectedReport(res.data);
      setView('view');
    } catch (err) {
      console.error('Failed to update report:', err);
      alert('Failed to update report');
    }
  };

  const handleDelete = async (reportId) => {
    try {
      await api.deleteReport(reportId);
      setReports(prev => prev.filter(r => r.report_id !== reportId));
      setDeleteConfirm(null);
    } catch (err) {
      console.error('Failed to delete report:', err);
      alert('Failed to delete report');
    }
  };

  if (view === 'create') {
    return (
      <div className="p-6">
        <ReportEditor
          isNew
          aiInsights={aiInsights}
          onSave={handleCreate}
          onCancel={() => {
            setView('list');
            setAiInsights(null);
          }}
        />
      </div>
    );
  }

  if (view === 'edit' && selectedReport) {
    return (
      <div className="p-6">
        <ReportEditor
          report={selectedReport}
          onSave={handleUpdate}
          onCancel={() => setView('view')}
        />
      </div>
    );
  }

  if (view === 'view' && selectedReport) {
    return (
      <div className="p-6">
        <ReportView
          report={selectedReport}
          onBack={() => {
            setSelectedReport(null);
            setView('list');
          }}
          onEdit={() => setView('edit')}
        />
      </div>
    );
  }

  return (
    <div className="p-6 space-y-6">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl font-bold text-gray-900">FAN Reports</h1>
          <p className="text-gray-500">Weekly IT status reports</p>
        </div>
        <Button onClick={() => setView('create')} className="gap-2 bg-blue-600 hover:bg-blue-700">
          <Plus className="h-4 w-4" />
          New Report
        </Button>
      </div>

      {loading ? (
        <div className="text-center py-12">
          <div className="animate-spin h-8 w-8 border-4 border-blue-500 border-t-transparent rounded-full mx-auto"></div>
          <p className="mt-2 text-gray-500">Loading reports...</p>
        </div>
      ) : reports.length === 0 ? (
        <Card>
          <CardContent className="py-12 text-center">
            <FileText className="h-12 w-12 text-gray-300 mx-auto mb-4" />
            <h3 className="text-lg font-medium text-gray-900">No reports yet</h3>
            <p className="text-gray-500 mt-1">Create your first FAN report to get started</p>
            <Button onClick={() => setView('create')} className="mt-4 gap-2">
              <Plus className="h-4 w-4" />
              Create Report
            </Button>
          </CardContent>
        </Card>
      ) : (
        <div className="grid gap-4">
          {reports.map((report) => (
            <Card key={report.report_id} className="hover:shadow-md transition-shadow">
              <CardContent className="p-4">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-4">
                    <div className="p-2 bg-blue-100 rounded-lg">
                      <FileText className="h-6 w-6 text-blue-600" />
                    </div>
                    <div>
                      <h3 className="font-semibold text-gray-900">{report.title}</h3>
                      <div className="flex items-center gap-3 text-sm text-gray-500">
                        <span className="flex items-center gap-1">
                          <Calendar className="h-3 w-3" />
                          Week of {report.week_of}
                        </span>
                        <span>Created {new Date(report.created_at).toLocaleDateString()}</span>
                      </div>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedReport(report);
                        setView('view');
                      }}
                      className="gap-1"
                    >
                      <Eye className="h-4 w-4" />
                      View
                    </Button>
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => {
                        setSelectedReport(report);
                        setView('edit');
                      }}
                      className="gap-1"
                    >
                      <Edit className="h-4 w-4" />
                      Edit
                    </Button>
                    <Dialog open={deleteConfirm === report.report_id} onOpenChange={(open) => !open && setDeleteConfirm(null)}>
                      <DialogTrigger asChild>
                        <Button
                          variant="ghost"
                          size="sm"
                          onClick={() => setDeleteConfirm(report.report_id)}
                          className="text-red-600 hover:text-red-700 hover:bg-red-50"
                        >
                          <Trash2 className="h-4 w-4" />
                        </Button>
                      </DialogTrigger>
                      <DialogContent>
                        <DialogHeader>
                          <DialogTitle>Delete Report?</DialogTitle>
                        </DialogHeader>
                        <p className="text-gray-600">
                          Are you sure you want to delete "{report.title}"? This action cannot be undone.
                        </p>
                        <div className="flex justify-end gap-2 mt-4">
                          <Button variant="outline" onClick={() => setDeleteConfirm(null)}>
                            Cancel
                          </Button>
                          <Button
                            variant="destructive"
                            onClick={() => handleDelete(report.report_id)}
                            className="bg-red-600 hover:bg-red-700"
                          >
                            Delete
                          </Button>
                        </div>
                      </DialogContent>
                    </Dialog>
                  </div>
                </div>
                {report.stats_snapshot && (
                  <div className="mt-3 flex flex-wrap gap-2 text-xs">
                    <span className="bg-gray-100 px-2 py-1 rounded">
                      {report.stats_snapshot.total_tickets} tickets
                    </span>
                    <span className="bg-red-100 text-red-700 px-2 py-1 rounded">
                      {report.stats_snapshot.open} open
                    </span>
                    <span className="bg-green-100 text-green-700 px-2 py-1 rounded">
                      {report.stats_snapshot.closed} closed
                    </span>
                  </div>
                )}
              </CardContent>
            </Card>
          ))}
        </div>
      )}
    </div>
  );
};

export default ReportsPage;
